import { Reorder } from 'framer-motion';
import React from 'react';

import { AccountTaxTypeGroup, AssetLocationPolicy, PolicyItem } from '@ndvr/portfolio-api';

import { GrabberIcon } from '@/components/Icons';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableSkeletonRow } from '@/components/ui/table-skeleton-row';
import { useAccountGroupProfileWithAumIncluded } from '@/views/portfolio/account-aum-queries';
import { titleCase, useRequiredParams } from 'utils';
import { getAllocationTypeLabel } from 'utils';
import {
    useDefaultAssetLocationPolicy,
    useUpdateAccountGroupAssetLocationPolicy,
} from 'views/portfolio/account-group-queries';
import { taxTypeMap } from 'views/portfolio/PortfolioPage/tabs/asset-location/constants';
import { useAccountGroupAllocations } from 'views/portfolio/PortfolioPage/tabs/target-allocation/allocationUtils';

import { resolveAssetLocationPolicy } from './resolve-asset-location-policy';
import { useAssetLocationEdit } from './useAssetLocationEdit';

export const AssetLocationTab = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { data: accountGroupProfile, isPending: isAccountGroupProfilePending } =
        useAccountGroupProfileWithAumIncluded(accountGroupGUID);
    const { data: defaultAssetLocationPolicy, isPending: isDefaultAssetLocationPolicyPending } =
        useDefaultAssetLocationPolicy();
    const { allocations, isPending: isAllocationsPending } = useAccountGroupAllocations(accountGroupProfile);

    const { mutate: updateAccountGroupAssetLocationPolicy, isPending: isSaving } =
        useUpdateAccountGroupAssetLocationPolicy(accountGroupGUID);

    const resolvedPolicyItems = resolveAssetLocationPolicy(
        allocations,
        defaultAssetLocationPolicy,
        accountGroupProfile?.accountGroup.assetLocationPolicy?.policyItems,
    );

    const { isEditMode, editablePolicies, handleEditClick, handlePolicyReorder, exitEditMode } =
        useAssetLocationEdit(resolvedPolicyItems);

    const handleSaveClick = () => {
        updateAccountGroupAssetLocationPolicy({ policyItems: editablePolicies } satisfies AssetLocationPolicy, {
            onSuccess: () => {
                exitEditMode();
            },
        });
    };

    const isLoading = isAccountGroupProfilePending || isDefaultAssetLocationPolicyPending || isAllocationsPending;

    const policyItems = isEditMode ? editablePolicies : resolvedPolicyItems;

    return (
        <div>
            <div className="mb-4 flex items-start justify-between">
                <div className="space-y-1">
                    <h2 className="text-cocoa-1000 text-xl">Asset Location Policy</h2>
                    <p className="text-muted-foreground text-sm">
                        Current policy rules for asset allocation across account types
                    </p>
                </div>
                <div className="flex gap-2">
                    {isEditMode ? (
                        <>
                            <Button variant="outline" onClick={exitEditMode}>
                                Cancel
                            </Button>
                            <Button onClick={handleSaveClick} disabled={isSaving}>
                                {isSaving ? <Spinner className="mr-2 h-4 w-4" /> : null}
                                Save
                            </Button>
                        </>
                    ) : (
                        <Button variant="outline" onClick={handleEditClick}>
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            <Card className="overflow-hidden rounded-md border">
                <Table className="w-full text-sm">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="border-b px-4 py-3 text-left font-medium text-black">
                                Strategy Priority
                            </TableHead>
                            <TableHead className="border-b px-4 py-3 text-left font-medium text-black">
                                Strategy
                            </TableHead>
                            <TableHead className="border-b px-4 py-3 text-left font-medium text-black">
                                Account Type Priority
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading
                            ? [1, 2, 3].map((i) => <TableSkeletonRow key={`loading-row-${i}`} columns={3} />)
                            : policyItems.map((policy, policyIndex) => (
                                  <PolicyRow
                                      key={policyIndex}
                                      policy={policy}
                                      policyIndex={policyIndex}
                                      isEditMode={isEditMode}
                                      onReorder={handlePolicyReorder}
                                  />
                              ))}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
};

const EditModePolicyCell = ({
    policy,
    policyIndex,
    onReorder,
}: {
    policy: PolicyItem;
    policyIndex: number;
    onReorder: (policyIndex: number, newOrder: AccountTaxTypeGroup[]) => void;
}) => (
    <Reorder.Group
        axis="x"
        values={policy.policy}
        onReorder={(newOrder: AccountTaxTypeGroup[]) => onReorder(policyIndex, newOrder)}
        className="flex text-sm"
    >
        {policy.policy.map((taxType: AccountTaxTypeGroup) => (
            <Reorder.Item
                key={taxType}
                value={taxType}
                className="bg-accent/30 hover:bg-accent/50 mr-3 flex cursor-grab items-center rounded p-2 active:cursor-grabbing"
            >
                <GrabberIcon className="mr-1 h-3 w-3" />
                {taxTypeMap[taxType] || titleCase(taxType)}
            </Reorder.Item>
        ))}
    </Reorder.Group>
);

const ViewModePolicyCell = ({ policy }: { policy: PolicyItem }) => (
    <div className="text-sm">
        {policy.policy.map((taxType: AccountTaxTypeGroup, taxTypeIndex: number) => (
            <span key={taxTypeIndex} className="mr-3 inline-block">
                <span className="mr-1 text-gray-500">{taxTypeIndex + 1}.</span>
                {taxTypeMap[taxType] || titleCase(taxType)}
            </span>
        ))}
    </div>
);

const PolicyRow = ({
    policy,
    policyIndex,
    isEditMode,
    onReorder,
}: {
    policy: PolicyItem;
    policyIndex: number;
    isEditMode: boolean;
    onReorder: (policyIndex: number, newOrder: AccountTaxTypeGroup[]) => void;
}) => {
    return (
        <TableRow>
            <TableCell className="border-b border-gray-100 px-4 py-3 font-mono text-gray-500">
                {policyIndex + 1}
            </TableCell>
            <TableCell className="border-b border-gray-100 px-4 py-3">
                {getAllocationTypeLabel(policy.growthAllocationType)}
            </TableCell>
            <TableCell className="border-b border-gray-100 px-4 py-3">
                {isEditMode ? (
                    <EditModePolicyCell policy={policy} policyIndex={policyIndex} onReorder={onReorder} />
                ) : (
                    <ViewModePolicyCell policy={policy} />
                )}
            </TableCell>
        </TableRow>
    );
};
